/**
 * Facility Action Required Popup Component
 *
 * Shows a popup notification to admin/supervisor users when facilities 
 * with unassigned staff are detected after login.
 * 
 * Styled consistently with ListModal.js design patterns.
 *
 * @component
 */

import React from 'react';
import { Button } from '@voicecare.ai/ui-library';
import { useTheme } from '../../ThemeContext';

const FacilityActionPopup = ({
  visible,
  facilitiesCount = 0,
  onContinue,
  loading = false
}) => {
  const { themeMode, currentTheme } = useTheme();

  if (!visible) return null;

  // Theme-based styling inspired by ListModal.js
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10000,
    animation: 'fadeIn 0.3s ease-out'
  };

  const modalStyle = {
    backgroundColor: themeMode === 'dark' ? '#04002b' : '#ffffff',
    color: currentTheme.text?.primary || currentTheme.homePage?.textColor,
    borderRadius: '16px',
    padding: '2rem',
    maxWidth: '480px',
    width: '90%',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: themeMode === 'dark' 
      ? '0 20px 40px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.1)'
      : '0 20px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
    border: themeMode === 'dark' ? '1px solid #302d4c' : '1px solid #e9e8ea',
    animation: 'slideIn 0.3s ease-out',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif",
    textAlign: 'center'
  };

  const iconContainerStyle = {
    marginBottom: '1.5rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const iconStyle = {
    fontSize: '4rem',
    color: '#faad14',
    lineHeight: 1
  };

  const titleStyle = {
    fontSize: '20px',
    fontWeight: '600',
    color: currentTheme.text?.primary || currentTheme.homePage?.textColor,
    margin: '0 0 1rem 0',
    lineHeight: '1.3'
  };

  const messageStyle = {
    fontSize: '16px',
    color: currentTheme.text?.primary || currentTheme.homePage?.textColor,
    lineHeight: '1.5',
    marginBottom: '0.5rem'
  };

  const submessageStyle = {
    fontSize: '14px',
    color: themeMode === 'dark' ? '#a0a0a0' : '#666666',
    lineHeight: '1.5',
    marginBottom: '2rem',
    opacity: 0.8
  };

  const buttonStyle = {
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    padding: '12px 32px',
    minWidth: '240px',
    transition: 'all 0.2s ease'
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideIn {
            from { 
              opacity: 0; 
              transform: translateY(-20px) scale(0.95); 
            }
            to { 
              opacity: 1; 
              transform: translateY(0) scale(1); 
            }
          }
        `}
      </style>
      <div style={overlayStyle}>
        <div style={modalStyle}>
          {/* Warning Icon */}
          <div style={iconContainerStyle}>
            <div style={iconStyle}>⚠️</div>
          </div>

          {/* Title */}
          <h2 style={titleStyle}>Action Required</h2>

          {/* Message */}
          <div style={messageStyle}>
            {facilitiesCount > 0 ? (
              <>
                You have <strong style={{ color: '#faad14' }}>{facilitiesCount} facilit{facilitiesCount === 1 ? 'y' : 'ies'}</strong> with unassigned staff members.
              </>
            ) : (
              <>
                You have facilities with unassigned staff members.
              </>
            )}
          </div>
          
          <div style={submessageStyle}>
            Staff members need to be assigned to departments to ensure proper organization and workflow.
          </div>

          {/* Action Button */}
          <Button
            label={loading ? "Loading..." : "Continue to Facility Management"}
            onClick={onContinue}
            disabled={loading}
            style={buttonStyle}
            backgroundColor={currentTheme.loginPage?.loginButtonColor || '#e63e2b'}
          />
        </div>
      </div>
    </>
  );
};

export default FacilityActionPopup;
